import type { JSX } from 'react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2, Upload, X } from 'lucide-react';
import type { Skill } from '@echo-work-platform/shared-types';
import { api, apiFetch, deleteErrorMessage } from '@/lib/api-client';
import { formatNumber } from '@/lib/utils';

type FormMode = { kind: 'closed' } | { kind: 'create' } | { kind: 'edit'; skill: Skill };

interface FormValues {
  name: string;
  description: string;
  bundleVersion: string;
}

export function Skills(): JSX.Element {
  const queryClient = useQueryClient();
  const skills = useQuery({
    queryKey: ['/api/admin/skills'],
    queryFn: () => api.get<Skill[]>('/api/admin/skills'),
  });
  const [mode, setMode] = useState<FormMode>({ kind: 'closed' });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/skills/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/api/admin/skills'] }),
    onError: (error: unknown) => alert(deleteErrorMessage(error)),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-neutral-900">Agents</h2>
        <button className="btn-action" onClick={() => setMode({ kind: 'create' })}>
          <Plus className="h-4 w-4" /> Új agent
        </button>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-2.5">Név</th>
              <th className="px-4 py-2.5">Leírás</th>
              <th className="px-4 py-2.5">Verzió / SHA</th>
              <th className="px-4 py-2.5">Méret</th>
              <th className="w-24 px-4 py-2.5">Műveletek</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {skills.isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-neutral-500">
                  Betöltés…
                </td>
              </tr>
            )}
            {skills.data?.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-neutral-500">
                  Nincs agent.
                </td>
              </tr>
            )}
            {skills.data?.map((s) => (
              <tr key={s.id} className="hover:bg-neutral-50">
                <td className="px-4 py-2.5 font-medium">{s.name}</td>
                <td className="px-4 py-2.5 text-neutral-600">{s.description ?? '—'}</td>
                <td className="px-4 py-2.5 font-mono text-xs">
                  <div>{s.bundleVersion}</div>
                  <div className="text-neutral-400">{s.bundleSha256.slice(0, 12)}</div>
                </td>
                <td className="px-4 py-2.5 text-xs">{formatNumber(s.bundleSizeBytes)} byte</td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1">
                    <button
                      title="Szerkeszt"
                      className="rounded p-1 text-neutral-500 hover:bg-neutral-100"
                      onClick={() => setMode({ kind: 'edit', skill: s })}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      title="Töröl"
                      className="rounded p-1 text-rose-500 hover:bg-rose-50"
                      onClick={() => {
                        if (confirm(`Törlés: "${s.name}"?`)) remove.mutate(s.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {mode.kind !== 'closed' && (
        <SkillFormModal
          mode={mode}
          onClose={() => setMode({ kind: 'closed' })}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['/api/admin/skills'] });
            setMode({ kind: 'closed' });
          }}
        />
      )}
    </div>
  );
}

function SkillFormModal({
  mode,
  onClose,
  onSaved,
}: {
  mode: Exclude<FormMode, { kind: 'closed' }>;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const initial = mode.kind === 'edit' ? mode.skill : null;
  const { register, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      name: initial?.name ?? '',
      description: initial?.description ?? '',
      bundleVersion: initial?.bundleVersion ?? '1.0.0',
    },
  });
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(v: FormValues) {
    setBusy(true);
    setError(null);
    try {
      if (mode.kind === 'edit') {
        await api.patch(`/api/admin/skills/${initial!.id}`, {
          name: v.name,
          description: v.description || null,
        });
        const file = fileRef.current?.files?.[0];
        if (file) {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('version', v.bundleVersion || '1.0.0');
          await apiFetch(`/api/admin/skills/${initial!.id}/bundle`, { method: 'POST', body: fd });
        }
      } else {
        const file = fileRef.current?.files?.[0];
        if (!file) throw new Error('Válassz egy zip-fájlt');
        const fd = new FormData();
        fd.append('file', file);
        fd.append('name', v.name);
        if (v.description) fd.append('description', v.description);
        fd.append('version', v.bundleVersion || '1.0.0');
        await apiFetch('/api/admin/skills', { method: 'POST', body: fd });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--overlay)' }}
    >
      <div className="card w-full max-w-2xl p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {mode.kind === 'edit' ? 'Agent szerkesztése' : 'Új agent (bundle)'}
          </h3>
          <button className="rounded p-1 text-neutral-400 hover:bg-neutral-100" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>
        <form className="space-y-4" onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
          <div>
            <label className="label">Név</label>
            <input className="input" {...register('name', { required: true })} />
          </div>
          <div>
            <label className="label">Rövid leírás</label>
            <input className="input" {...register('description')} />
          </div>
          <div className="space-y-3 rounded-md border border-neutral-200 bg-neutral-50 p-3">
            <div>
              <label className="label">Verzió (semver)</label>
              <input className="input" placeholder="1.0.0" {...register('bundleVersion')} />
            </div>
            <div>
              <label className="label">
                Zip fájl (max 20 MB, kötelező SKILL.md a rootban)
                {mode.kind === 'create' && <span className="text-rose-500"> *</span>}
              </label>
              <input
                ref={fileRef}
                type="file"
                accept=".zip,application/zip"
                className="block w-full text-sm"
              />
            </div>
            {mode.kind === 'edit' && (
              <>
                <p className="text-xs text-neutral-600">
                  Aktuális: <span className="font-mono">{initial!.bundleFilename}</span> · v
                  {initial!.bundleVersion} · SHA {initial!.bundleSha256.slice(0, 12)} ·{' '}
                  {formatNumber(initial!.bundleSizeBytes)} byte
                </p>
                <p className="text-xs text-neutral-500">
                  Üresen hagyva a meglévő zip megmarad. Új zip-fájl feltöltésével felülíródik.
                </p>
              </>
            )}
          </div>
          {error && <p className="text-sm text-rose-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
              Mégse
            </button>
            <button type="submit" className="btn-action" disabled={busy}>
              {busy ? (
                '…'
              ) : (
                <>
                  <Upload className="h-4 w-4" /> Mentés
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
